import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ListChecks, ReceiptText, SearchX } from 'lucide-react'
import { toast } from 'sonner'
import {
  bulkTransactions,
  deleteTransaction,
  deleteTransfer,
  type TransactionSummary,
} from '@/api/transactions'
import { listAccounts } from '@/api/accounts'
import { listCategories } from '@/api/categories'
import { deleteCashEntry, deleteSettlement } from '@/api/people'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useSampleHousehold } from '@/hooks/useSampleHousehold'
import { cn } from '@/lib/utils'
import type { useTransactionPages } from '@/hooks/useTransactionPages'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { BulkActionBar } from './bulk/BulkActionBar'
import { TransactionRow } from './TransactionRow'
import { groupRowsByDay, type RowModel } from './rowModel'
import { errorText } from './add/formLogic'
import { dayLabel } from './transactionDisplay'
import {
  AddTransactionModal,
  DeleteConfirmDialog,
  EditTransactionModal,
  EditTransferModal,
} from './TransactionForms'

export function TransactionList({
  pages,
  hasFilters,
  onClearFilters,
  compact = false,
  selectable = false,
  selecting: selectingProp,
  onSelectingChange,
  hideSelectButton = false,
  focusAccountId,
}: {
  pages: ReturnType<typeof useTransactionPages>
  /** Offers a Select mode for changing or deleting many rows at once. */
  selectable?: boolean
  /** Lets the page own select mode, e.g. to start it from a button outside the list. */
  selecting?: boolean
  onSelectingChange?: (selecting: boolean) => void
  /** The page shows its own Select button. */
  hideSelectButton?: boolean
  /** Changes the empty state to "no matches" with a Clear filters action. */
  hasFilters: boolean
  onClearFilters?: () => void
  compact?: boolean
  /** The list is one account's view: a multi-account expense shows that account's part. */
  focusAccountId?: string
}) {
  const format = useListAmountFormat()
  const queryClient = useQueryClient()
  const [editTx, setEditTx] = useState<TransactionSummary | null>(null)
  const [deleteTx, setDeleteTx] = useState<TransactionSummary | null>(null)
  const [refundTx, setRefundTx] = useState<TransactionSummary | null>(null)
  const [duplicateTx, setDuplicateTx] = useState<TransactionSummary | null>(null)
  const isReadOnly = useIsReadOnly()
  const { isSample } = useCurrentHousehold()
  const { sample, startCreate, dialogs: sampleDialogs } = useSampleHousehold()
  const canExploreSample = !compact && !isSample && !sample
  const [ownSelecting, setOwnSelecting] = useState(false)
  const selecting = selectingProp ?? ownSelecting
  const setSelecting = onSelectingChange ?? setOwnSelecting
  // Row keys (a transfer's or an EMI's folded row counts once); each maps to one transaction id.
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data: accountsData } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })
  const accounts = useMemo(() => accountsData?.accounts ?? [], [accountsData])
  const accountTypes = useMemo(() => new Map(accounts.map((a) => [a.id, a.type])), [accounts])
  const categories = categoriesData?.categories ?? []

  const days = useMemo(
    () => groupRowsByDay(pages.transactions, format, focusAccountId),
    [pages.transactions, format, focusAccountId]
  )

  const deleteMutation = useMutation({
    mutationFn: (tx: TransactionSummary) => {
      if (tx.type === 'TRANSFER' && tx.transferId) return deleteTransfer(tx.transferId)
      if (tx.type === 'PERSON_PAYMENT' && tx.settlementId) return deleteSettlement(tx.settlementId)
      if (tx.type === 'PERSON_PAYMENT') return deleteCashEntry(tx.id)
      return deleteTransaction(tx.id)
    },
    onSuccess: () => {
      for (const key of [
        'transactions',
        'accounts',
        'contacts',
        'owed-summary',
        'dashboard',
        'budget',
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      setDeleteTx(null)
    },
  })

  const reconcileMutation = useMutation({
    mutationFn: (row: RowModel) =>
      bulkTransactions({ ids: row.ids, action: 'SET_RECONCILED', reconciled: !row.reconciled }),
    onSuccess: (_, row) => {
      for (const key of ['transactions', 'accounts'])
        queryClient.invalidateQueries({ queryKey: [key] })
      toast.success(row.reconciled ? 'Marked not reconciled' : 'Marked reconciled')
    },
    onError: (err) => toast.error(errorText(err)),
  })

  function stopSelecting() {
    setSelecting(false)
    setSelected(new Set())
  }

  if (pages.isLoading) return <p className="text-sm text-[var(--fl-text-2)]">Loading…</p>
  if (pages.isError) {
    return <p className="text-sm text-[var(--fl-error)]">Failed to load transactions.</p>
  }

  return (
    <>
      {sampleDialogs}
      {days.length === 0 ? (
        <div className="rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] shadow-[var(--card-shadow-sm)]">
          {hasFilters ? (
            <EmptyState
              compact={compact}
              icon={SearchX}
              title="No transactions match these filters"
              description="Try a wider period, or remove a filter."
              secondaryAction={
                onClearFilters ? { label: 'Clear filters', onClick: onClearFilters } : undefined
              }
            />
          ) : (
            <EmptyState
              compact={compact}
              icon={ReceiptText}
              title="Nothing recorded for this period"
              description="Record what you spent or received — it starts shaping your monthly picture right away."
              secondaryAction={
                canExploreSample
                  ? { label: 'Explore sample data', onClick: startCreate }
                  : undefined
              }
            />
          )}
        </div>
      ) : (
        <div className={cn('flex flex-col gap-3 md:gap-5', selecting && 'pb-24')}>
          {selectable && !isReadOnly && !selecting && !hideSelectButton && (
            <div className="-mb-2 flex justify-end">
              <Button
                variant="ghost"
                onClick={() => setSelecting(true)}
                className="h-9 gap-1.5 rounded-full px-3 text-sm font-medium text-[var(--fl-primary)]"
              >
                <ListChecks size={16} aria-hidden="true" /> Select
              </Button>
            </div>
          )}
          {days.map((day) => (
            <section
              key={day.date}
              aria-label={dayLabel(day.date)}
              className="flex flex-col gap-1.5 md:gap-2"
            >
              <div className="flex items-baseline justify-between gap-3 px-1">
                {selecting ? (
                  <label className="flex cursor-pointer items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fl-text-3)] md:text-[11.5px]">
                    <input
                      type="checkbox"
                      aria-label={`Select all on ${dayLabel(day.date)}`}
                      checked={day.rows.every((r) => selected.has(r.key))}
                      onChange={(e) => {
                        const next = new Set(selected)
                        for (const r of day.rows) {
                          if (e.target.checked) next.add(r.key)
                          else next.delete(r.key)
                        }
                        setSelected(next)
                      }}
                      className="h-4 w-4 accent-[var(--fl-primary)]"
                    />
                    {dayLabel(day.date)}
                  </label>
                ) : (
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fl-text-3)] md:text-[11.5px]">
                    {dayLabel(day.date)}
                  </h3>
                )}
                <p className="text-xs tabular-nums text-[var(--fl-text-2)] md:text-[12.5px]">
                  {[
                    day.income > 0 ? `in ${format(day.income)}` : null,
                    day.spent > 0
                      ? `spent ${format(day.spent)}`
                      : day.spent < 0
                        ? `refunds ${format(-day.spent)}`
                        : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="divide-y divide-[var(--fl-divider)] overflow-hidden rounded-xl border md:rounded-2xl border-[var(--fl-border)] bg-[var(--fl-surface)] shadow-[var(--card-shadow-sm)]">
                {day.rows.map((row) => (
                  <TransactionRow
                    key={row.key}
                    row={row}
                    accountTypes={accountTypes}
                    onEdit={setEditTx}
                    onDuplicate={setDuplicateTx}
                    onDelete={setDeleteTx}
                    onRefund={setRefundTx}
                    onReconcile={(r) => reconcileMutation.mutate(r)}
                    selection={
                      selecting
                        ? {
                            checked: selected.has(row.key),
                            onToggle: () => {
                              const next = new Set(selected)
                              if (next.has(row.key)) next.delete(row.key)
                              else next.add(row.key)
                              setSelected(next)
                            },
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </section>
          ))}
          {pages.hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={pages.loadMore}
                disabled={pages.isLoadingMore}
                className="h-10 rounded-full px-5"
              >
                {pages.isLoadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}

      {selecting && (
        <BulkActionBar
          selectedIds={days
            .flatMap((d) => d.rows)
            .filter((r) => selected.has(r.key))
            .map((r) => r.tx.id)}
          reconcileIds={days
            .flatMap((d) => d.rows)
            .filter((r) => selected.has(r.key))
            .flatMap((r) => r.ids)}
          onDone={stopSelecting}
          onCancel={stopSelecting}
        />
      )}
      {duplicateTx && (
        <AddTransactionModal
          accounts={accounts}
          categories={categories}
          duplicateOf={duplicateTx}
          onClose={() => setDuplicateTx(null)}
        />
      )}
      {refundTx && (
        <AddTransactionModal
          accounts={accounts}
          categories={categories}
          refundTarget={refundTx}
          onClose={() => setRefundTx(null)}
        />
      )}
      {editTx && editTx.type === 'TRANSFER' && (
        <EditTransferModal
          tx={editTx}
          onClose={() => setEditTx(null)}
          onDelete={() => {
            setDeleteTx(editTx)
            setEditTx(null)
          }}
        />
      )}
      {editTx && editTx.type !== 'TRANSFER' && (
        <EditTransactionModal
          tx={editTx}
          accounts={accounts}
          categories={categories}
          onClose={() => setEditTx(null)}
          onDelete={() => {
            setDeleteTx(editTx)
            setEditTx(null)
          }}
        />
      )}
      {deleteTx && (
        <DeleteConfirmDialog
          tx={deleteTx}
          onConfirm={() => deleteMutation.mutate(deleteTx)}
          onCancel={() => setDeleteTx(null)}
          isPending={deleteMutation.isPending}
          errorMessage={
            deleteMutation.isError
              ? ((deleteMutation.error as { response?: { data?: { detail?: string } } })?.response
                  ?.data?.detail ?? 'Something went wrong. Please try again.')
              : undefined
          }
        />
      )}
    </>
  )
}
